import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  CloudOff,
  LoaderCircle,
  Lock,
  Plus,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import { puedeOverrideEnObra, puedeRegistrarEnObra } from "@/app/guards";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ApiError } from "@/lib/api";
import { canManageObra, useMe } from "@/lib/auth";
import {
  type BloqueoDetalle,
  type LotoDetail,
  type PuntoDeBloqueo,
  textoPuntos,
  useAltaLotoRechazada,
  useCerrarLoto,
  useLoto,
} from "@/lib/lotos";
import { MENSAJE_REQUIERE_CONEXION, puedeRetirarSinConexion } from "@/lib/offline-policy";
import { useEstadoConexion } from "@/lib/online-status";
import { etiquetaProcedimiento } from "@/lib/procedimientos";
import { traducirEstadoServidor } from "@/lib/sync-estado";
import { AvisoDocumentacionAcreditativa } from "./AvisoDocumentacionAcreditativa";
import { CambiarProcedimientoDialog } from "./CambiarProcedimientoDialog";
import { ColocarWizard } from "./ColocarWizard";
import { EstadoLotoBadge } from "./estado";
import { type GaleriaImagen, Lightbox } from "./Lightbox";
import { RegistroFotografico } from "./RegistroFotografico";
import { ReportePdfButton } from "./ReportePdfButton";
import { RetirarDialog, type RetiroPunto } from "./RetirarDialog";

// formatFecha da un instante ISO en formato local es-ES (día y hora). "—" si no es válido.
function formatFecha(iso: string | null): string {
  if (!iso) {
    return "—";
  }
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) {
    return "—";
  }
  return fecha.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Motivos de las acciones vetadas por PROPIEDAD (#255 D11). El servidor es el guardián real
// (403 CIERRE_NO_PERMITIDO / RETIRO_NO_PERMITIDO / CAMBIO_PROC_NO_PERMITIDO); la ficha se
// limita a explicar por qué el botón está apagado, en vez de dejar chocar contra el error.
const MOTIVO_CIERRE_AJENO = "Solo el creador o un supervisor puede cerrar este LOTO.";
const MOTIVO_PROCEDIMIENTO_AJENO =
  "Solo el creador o un supervisor puede cambiar el procedimiento.";
const MOTIVO_RETIRO_AJENO = "Solo quien colocó el candado o un supervisor puede retirarlo.";

// Motivo del retiro vetado por CONECTIVIDAD, visible en la tarjeta del candado y no solo en el
// `title` (#356 D2): un tooltip no llega al operario con guantes, el mismo argumento con el que
// #339 bajó a texto el motivo del cierre y del cambio de procedimiento. Va aquí abajo y no en el
// aviso general porque la asimetría es POR CANDADO: el PROPIO sí se retira sin red (política
// offline, fuente única), así que nombrarlo arriba sería falso en el caso más frecuente.
// Es un enunciado de REQUISITO —como `MENSAJE_REQUIERE_CONEXION`—, cierto en los tres estados:
// no afirma que falte la red, que es justo lo que durante la ventana sin confirmar todavía no se
// sabe (#263), y dice «ajeno» de forma explícita para que no se lea como «retirar exige conexión».
const MOTIVO_RETIRO_AJENO_SIN_CONEXION = "Retirar un candado ajeno requiere conexión.";

// mensajeAccion traduce el fallo del cierre —única transición del ciclo binario— a español.
function mensajeAccion(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.code === "LOTO_TIENE_CANDADOS_ACTIVOS") {
      return "No se puede cerrar: aún hay candados activos.";
    }
    if (err.code === "CIERRE_NO_PERMITIDO") {
      return "No puedes cerrar este LOTO: debes ser quien lo abrió o un supervisor autorizado.";
    }
    if (err.code === "TRANSICION_INVALIDA") {
      return "El LOTO ya está cerrado.";
    }
    return err.message || fallback;
  }
  return fallback;
}

/**
 * LotoDetailPage es la ficha operativa de un LOTO: cabecera con estado y equipo, un banner
 * de seguridad muy visible (equipo BLOQUEADO cuando hay candados colgados), la pila de
 * bloqueos como «etiquetas rojas» (activas destacadas, retiradas atenuadas) y las acciones
 * del ciclo de vida (cierre). Las mutaciones solo se muestran a quien puede registrar
 * operación en la obra (regla puede_registrar de la obra); el backend es el guardián real.
 */
export function LotoDetailPage() {
  const { id } = useParams({ strict: false });
  const lotoId = Number(id);
  const { data: me } = useMe();
  const obraId = useObraId();
  const puedeRegistrar = me != null && puedeRegistrarEnObra(me, obraId);
  const puedeBorrarFotos = me != null && canManageObra(me, obraId);
  // Override de propiedad (#255 D11): ADMIN, ADMIN_OBRA o CUALIFICADO de la obra pueden
  // actuar sobre trabajo AJENO (cerrar, cambiar procedimiento, retirar). Espejo exacto de
  // loto/handler.go::puedeOverrideEnObra.
  const puedeOverride = me != null && puedeOverrideEnObra(me, obraId);
  const estadoConexion = useEstadoConexion();

  const { data: loto, isPending, isError, error } = useLoto(obraId, lotoId);

  // #306 — Alta rechazada de un LOTO creado sin red. Si el id es TEMPORAL (negativo) y el servidor
  // rechazó su alta al sincronizar, esta ficha describe una intervención que no existe ni existirá:
  // se sustituye por el aviso con el motivo. El runner solo deja el estado observable (retira la
  // lectura optimista y conserva la mutación); NAVEGAR es cosa de aquí, porque `lib/` no navega.
  //
  // Y navega SOLO cuando el operario lo pide (D7): el aviso ESPERA. Un efecto que salta al centro
  // de sincronización en el mismo render en que se detecta el rechazo deja un aviso que no lee
  // nadie —y al técnico en otra pantalla sin saber por qué—, justo contra el argumento de #339 y
  // #356 con el que existe todo esto: la razón tiene que llegar al operario con guantes.
  const rechazo = useAltaLotoRechazada(lotoId);
  const navigate = useNavigate();

  const cerrar = useCerrarLoto(obraId);

  const [accionError, setAccionError] = useState<string | null>(null);
  const [abrirWizard, setAbrirWizard] = useState(false);
  const [abrirProcedimiento, setAbrirProcedimiento] = useState(false);
  // #245: lo que se retira es un PUNTO dentro de su candado, no el candado entero.
  const [aRetirar, setARetirar] = useState<RetiroPunto | null>(null);

  const volver = (
    <Link
      to="/obras/$obraId/lotos"
      params={{ obraId: String(obraId) }}
      className="inline-flex items-center gap-1 font-medium text-primary-700 text-sm hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Volver a LOTOs
    </Link>
  );

  // El rechazo del alta manda sobre cualquier estado de la consulta: la ficha temporal está muerta
  // aunque su lectura optimista siga en vuelo, así que se sustituye aquí y no se llega a pintar el
  // mensaje del caso bueno («ya se sincronizó»), que en un rechazo sería falso. El motivo es el que
  // el centro de sincronización ya redacta para ese `error_code` (fuente única, sin copy nueva) y
  // el `resumen` de la mutación es lo que nombra al LOTO: creado sin red, aún no tiene número de
  // servidor. Va en TEXTO, no en un `title`: un tooltip no llega al operario con guantes (#339).
  //
  // El viaje al centro de sincronización es una ACCIÓN EXPLÍCITA (D7): el aviso se queda mientras
  // el operario no la pulse. La ruta es GLOBAL —`/sincronizacion`, hija de `appLayoutRoute`—
  // porque la outbox es del dispositivo, no de la obra (D8).
  if (rechazo != null) {
    const intencion = rechazo.resumen?.trim();
    return (
      <div className="flex flex-col gap-4">
        {volver}
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {`No se pudo registrar este LOTO${intencion ? `: «${intencion}»` : ""}. ` +
              `${traducirEstadoServidor(rechazo.status, rechazo.errorCode)} ` +
              "El rechazo sigue en el centro de sincronización."}
          </span>
        </Alert>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void navigate({ to: "/sincronizacion" })}>
            <RefreshCw className="h-5 w-5" aria-hidden="true" />
            Ver en sincronización
          </Button>
        </div>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        {volver}
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando LOTO…
        </div>
      </div>
    );
  }

  if (isError) {
    const noEncontrado = error instanceof ApiError && error.code === "NO_ENCONTRADO";
    // Un id NEGATIVO que ya no resuelve = LOTO creado sin conexión que se sincronizó: su id
    // temporal no existe en el servidor y su número/id definitivos están ya en la lista.
    const esTemporal = lotoId < 0;
    return (
      <div className="flex flex-col gap-4">
        {volver}
        <Alert variant={esTemporal ? "info" : "destructive"}>
          {esTemporal ? (
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          ) : (
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          )}
          <span>
            {esTemporal
              ? "Este LOTO se creó sin conexión y ya se sincronizó. Búscalo en la lista con su número definitivo."
              : noEncontrado
                ? "Este LOTO no existe o ya no está disponible."
                : "No se pudo cargar el LOTO. Inténtalo de nuevo."}
          </span>
        </Alert>
      </div>
    );
  }

  const bloqueosActivos = loto.bloqueos.filter((b) => b.activo);
  const hayActivos = bloqueosActivos.length > 0;

  // Procedimiento de aislamiento del LOTO (#236): lo PROYECTA el servidor en la ficha, con sus
  // pasos y su criterio de energía cero. La cabecera lo lee de ahí, no del catálogo de maestros
  // (D5), así que ya no existe el estado «#id (no disponible)»: un procedimiento dado de baja
  // sigue llegando, marcado `activo: false`, y se muestra con su código y versión.
  // La cabecera NO gana marca de estado (#303 R6): un procedimiento retirado se lee con su
  // código y su versión, y el aviso vive donde se opera, en la sección de pasos.
  const procedimiento = loto.procedimiento;
  const procedimientoTexto = procedimiento
    ? etiquetaProcedimiento(procedimiento)
    : "Sin procedimiento";

  async function correr(fn: () => Promise<unknown>, fallback: string) {
    setAccionError(null);
    try {
      await fn();
    } catch (err) {
      setAccionError(mensajeAccion(err, fallback));
    }
  }

  const cierrePendiente = cerrar.isPending;
  // Sin conexión, el cierre se deshabilita: declara el equipo liberado y exige la autoridad del
  // servidor (política offline, #53). Solo colocar/retirar candado propio se permite offline.
  // Colocar sigue disponible aquí abajo.
  //
  // El GATE es binario y fail-closed (#237): todo lo que no sea red CONFIRMADA lo cierra, la
  // ventana «sin confirmar» del arranque incluida. El AVISO, en cambio, distingue los dos
  // motivos (#263): durante esa ventana no se sabe si hay red, y afirmar que no la hay
  // contradiría al indicador de la cabecera con algo todavía sin resolver.
  const sinConexion = estadoConexion !== "online";
  const conexionSinConfirmar = estadoConexion === "sin_confirmar";
  // Propiedad del LOTO (#255 D11): lo abrió este usuario, o tiene override. creado_por puede
  // ser null (su usuario se borró): entonces solo el supervisor puede cerrarlo o cambiarle el
  // procedimiento, igual que resuelve el servicio.
  const esCreador = loto.creado_por != null && loto.creado_por === me?.id;
  const puedeSobreEsteLoto = esCreador || puedeOverride;
  // Motivo por el que el cierre está vetado, en orden de prioridad: sin red > sin permiso >
  // candados activos. null ⇒ el botón va habilitado.
  const motivoCierre = sinConexion
    ? MENSAJE_REQUIERE_CONEXION
    : !puedeSobreEsteLoto
      ? MOTIVO_CIERRE_AJENO
      : hayActivos
        ? "Retira todos los candados activos antes de cerrar el LOTO."
        : null;

  return (
    <div className="flex flex-col gap-4">
      {volver}

      {/* Cabecera del LOTO */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-bold text-2xl text-slate-900 tabular-nums tracking-tight">
              {loto.numero_loto}
            </span>
            <EstadoLotoBadge estado={loto.estado} />
            {loto.id < 0 ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 font-semibold text-amber-800 text-xs">
                <CloudOff className="h-3.5 w-3.5" aria-hidden="true" />
                Sin sincronizar
              </span>
            ) : null}
            {/* Reporte PDF (#213): solo para LOTOs ya sincronizados (id positivo). */}
            {loto.id > 0 ? (
              <div className="ml-auto">
                <ReportePdfButton obraId={obraId} lotoId={loto.id} numeroLoto={loto.numero_loto} />
              </div>
            ) : null}
          </div>
          <div className="flex flex-col gap-0.5 text-slate-500">
            <span>
              <span className="font-medium text-slate-900">{loto.equipo}</span> ·{" "}
              {loto.codigo_equipo}
            </span>
            <span className="text-sm">{loto.area}</span>
          </div>
        </CardHeader>
        <CardContent className="gap-2 text-sm">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-slate-600">
            <span>
              <span className="text-slate-400">Apertura:</span> {formatFecha(loto.fecha_apertura)}
            </span>
            {loto.fecha_cierre ? (
              <span>
                <span className="text-slate-400">Cierre:</span> {formatFecha(loto.fecha_cierre)}
              </span>
            ) : null}
            <span>
              <span className="text-slate-400">Procedimiento:</span> {procedimientoTexto}
            </span>
          </div>
          {loto.descripcion ? (
            <p className="text-slate-600">
              <span className="text-slate-400">Descripción: </span>
              {loto.descripcion}
            </p>
          ) : null}
          {loto.observaciones ? (
            <p className="text-slate-600">
              <span className="text-slate-400">Observaciones: </span>
              {loto.observaciones}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* #435 (R12): por qué no hay acciones de operar si falta la documentación acreditativa. */}
      <AvisoDocumentacionAcreditativa me={me} obraId={obraId} />

      {/* Banner de seguridad: prioriza el aviso de equipo bloqueado. */}
      {hayActivos ? (
        <Alert variant="destructive">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span className="font-semibold">
            {bloqueosActivos.length} candado(s) activo(s) — equipo BLOQUEADO
          </span>
        </Alert>
      ) : loto.estado !== "CERRADO" ? (
        <Alert variant="success">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>Sin candados activos en este equipo.</span>
        </Alert>
      ) : (
        <Alert variant="info">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>LOTO cerrado. Aislamiento retirado.</span>
        </Alert>
      )}

      {/* Acciones del ciclo de vida (solo para quien puede registrar). */}
      {puedeRegistrar ? (
        <div className="flex flex-col gap-2">
          {accionError ? (
            <Alert variant="destructive">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>{accionError}</span>
            </Alert>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {loto.estado !== "CERRADO" ? (
              <Button variant="outline" onClick={() => setAbrirWizard(true)}>
                <Plus className="h-5 w-5" aria-hidden="true" />
                Colocar candado
              </Button>
            ) : null}
            {loto.estado !== "CERRADO" ? (
              <Button
                variant="outline"
                onClick={() => setAbrirProcedimiento(true)}
                disabled={sinConexion || !puedeSobreEsteLoto}
                title={
                  sinConexion
                    ? MENSAJE_REQUIERE_CONEXION
                    : !puedeSobreEsteLoto
                      ? MOTIVO_PROCEDIMIENTO_AJENO
                      : undefined
                }
              >
                <ClipboardList className="h-5 w-5" aria-hidden="true" />
                Cambiar procedimiento
              </Button>
            ) : null}
            {loto.estado === "ABIERTO" ? (
              <Button
                onClick={() =>
                  void correr(() => cerrar.mutateAsync(loto.id), "No se pudo cerrar el LOTO.")
                }
                disabled={cierrePendiente || motivoCierre !== null}
                title={motivoCierre ?? undefined}
              >
                <Lock className="h-5 w-5" aria-hidden="true" />
                Cerrar LOTO
              </Button>
            ) : null}
          </div>
          {/* El motivo también en texto: un tooltip no llega al operario con guantes. Mientras
              el heartbeat no confirma (#237) el motivo es «se está comprobando», no «no hay
              red»: el mismo mensaje y el mismo icono en movimiento que la píldora de la
              cabecera, para que las dos digan lo mismo (#263). El botón sigue apagado.
              Y el aviso NOMBRA LA CONSECUENCIA (#339 D3, que enmienda la D1 de #263): decir solo
              «Comprobando conexión…» dejaba únicamente en el `title` —justo donde no llega— qué
              se ha deshabilitado. Nombra las dos acciones que ESTA botonera guarda por
              conectividad; el retiro no cabe en un aviso general porque es por candado y el
              PROPIO sí se retira sin red (política offline, fuente única): lleva su motivo en su
              propia tarjeta.
              Y lleva la MISMA guarda de estado que las dos ramas siguientes (#356 D1): en un LOTO
              CERRADO ninguno de los dos botones que nombra llega a renderizarse, así que avisar
              de que «no están disponibles» sería ruido, y además falso —no es que no estén
              disponibles: es que no están—.
              #279 R8: las DOS variantes nombran las dos acciones. La de offline confirmado solo
              hablaba del cierre, y en ese estado «Cambiar procedimiento» está igual de apagado
              (`sinConexion || !puedeSobreEsteLoto`): el dueño del LOTO sin red lo pulsaba y el
              único texto visible hablaba de otro botón. Es UNA línea, no dos (la spec §4.1 no
              duplica el mensaje sin red), y la frase del cierre se conserva literal: la segunda
              se AÑADE detrás. */}
          {loto.estado !== "CERRADO" && sinConexion ? (
            <p className="flex items-center gap-1.5 text-slate-500 text-sm">
              {conexionSinConfirmar ? (
                <RefreshCw className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
              ) : (
                <CloudOff className="h-4 w-4 shrink-0" aria-hidden="true" />
              )}
              {conexionSinConfirmar
                ? "Comprobando conexión… El cierre del LOTO y el cambio de procedimiento no están disponibles hasta confirmarla."
                : "Sin conexión: el cierre del LOTO requiere conexión. El cambio de procedimiento tampoco está disponible."}
            </p>
          ) : loto.estado !== "CERRADO" && !puedeSobreEsteLoto ? (
            // Dos botones apagados por la misma causa, pero DOS acciones: cada una nombra la
            // suya (#279). Hasta ahora solo se leía el motivo del cierre, así que quien pulsaba
            // «Cambiar procedimiento» encontraba abajo una frase que hablaba de otro botón; el
            // motivo del procedimiento vivía únicamente en el `title`, donde no llega el
            // operario con guantes sobre una tablet (el mismo argumento de #339 y #382).
            <>
              <p className="text-slate-500 text-sm">{MOTIVO_CIERRE_AJENO}</p>
              <p className="text-slate-500 text-sm">{MOTIVO_PROCEDIMIENTO_AJENO}</p>
            </>
          ) : loto.estado !== "CERRADO" && hayActivos ? (
            <p className="text-slate-500 text-sm">
              Retira todos los candados activos antes de cerrar el LOTO.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Procedimiento de aislamiento (#236): la instrucción, a mano durante la intervención. */}
      <SeccionProcedimiento procedimiento={procedimiento} />

      {/* Pila de bloqueos (etiquetas rojas). */}
      <section className="flex flex-col gap-3">
        <h2 className="font-bold text-slate-900 text-xl tracking-tight">Candados del LOTO</h2>
        {loto.bloqueos.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-10 text-center text-slate-500">
            Aún no se ha colocado ningún candado.
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {loto.bloqueos.map((b) => (
              <BloqueoCard
                key={b.id}
                bloqueo={b}
                obraId={obraId}
                puedeRegistrar={puedeRegistrar}
                // Sin conexión solo se puede retirar un candado PROPIO; el retiro ajeno (override)
                // exige la autoridad del servidor y se deshabilita con «Requiere conexión».
                retiroRequiereConexion={
                  sinConexion && !puedeRetirarSinConexion(b.colocado_por, me?.id)
                }
                // #255 D11: el candado ajeno solo lo retira un supervisor (override); a los
                // demás se les muestra el botón apagado con el motivo, no un 403 al pulsar.
                retiroNoPermitido={
                  !puedeOverride && !(b.colocado_por != null && b.colocado_por === me?.id)
                }
                onRetirarPunto={(punto) => setARetirar({ bloqueo: b, punto })}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Registro fotográfico del LOTO + galería agregada (fotos del LOTO y de sus candados). */}
      <RegistroFotografico
        loto={loto}
        obraId={obraId}
        puedeRegistrar={puedeRegistrar}
        puedeBorrar={puedeBorrarFotos}
      />

      {/* Asistente de colocación, cambio de procedimiento y diálogo de retirada. */}
      <ColocarWizard loto={loto} open={abrirWizard} onClose={() => setAbrirWizard(false)} />
      <CambiarProcedimientoDialog
        loto={loto}
        open={abrirProcedimiento}
        onClose={() => setAbrirProcedimiento(false)}
      />
      <RetirarDialog retiro={aRetirar} idLoto={loto.id} onClose={() => setARetirar(null)} />
    </div>
  );
}

/**
 * SeccionProcedimiento muestra el procedimiento de aislamiento del LOTO (#236): la secuencia
 * ORDENADA de cortes y el criterio de verificación de energía cero, que es lo que el técnico
 * necesita leer mientras opera. Es una sección PLEGABLE y abierta por defecto (D3): abierta
 * para que la instrucción se vea sin buscarla; plegable para que no estorbe en el móvil cuando
 * ya se ha leído. Sin procedimiento no se pinta una sección vacía, sino una línea que lo dice.
 * Todo viene del bloque que proyecta el servidor: aquí no se resuelve ningún id.
 */
function SeccionProcedimiento({ procedimiento }: { procedimiento: LotoDetail["procedimiento"] }) {
  const [abierto, setAbierto] = useState(true);

  if (!procedimiento) {
    return (
      <p className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-3 text-slate-500 text-sm">
        Este LOTO no tiene procedimiento asignado.
      </p>
    );
  }

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className="flex items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
      >
        <ClipboardList className="h-5 w-5 shrink-0 text-primary-700" aria-hidden="true" />
        {/* El código y la versión NO se repiten aquí: ya están en la cabecera del LOTO, unos
            centímetros más arriba, y duplicarlos solo añade ruido en pantalla. */}
        <span className="font-bold text-slate-900 text-xl tracking-tight">
          Procedimiento de aislamiento
        </span>
        {abierto ? (
          <ChevronUp className="ml-auto h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
        ) : (
          <ChevronDown className="ml-auto h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
        )}
      </button>

      {abierto ? (
        <div className="flex flex-col gap-3">
          {/* Un procedimiento dado de baja DESPUÉS de abrir el LOTO se sigue leyendo (es el que
              rigió la intervención), pero se avisa de que ya no es la revisión vigente. El
              aviso usa la MISMA palabra que la etiqueta y que el servidor: «retirado» (#303). */}
          {!procedimiento.activo ? (
            <Alert variant="destructive">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>Este procedimiento ha sido retirado y ya no es la revisión vigente.</span>
            </Alert>
          ) : null}

          {procedimiento.pasos.length > 0 ? (
            <ol
              aria-label="Pasos del procedimiento"
              className="flex list-decimal flex-col gap-2 pl-6 text-base text-slate-700 marker:font-semibold marker:text-slate-400"
            >
              {procedimiento.pasos.map((paso, i) => (
                // Los pasos son texto libre y pueden repetirse: el índice es la identidad real
                // de la posición dentro de una lista ORDENADA e inmutable en pantalla.
                // biome-ignore lint/suspicious/noArrayIndexKey: el orden ES la identidad del paso
                <li key={i}>{paso}</li>
              ))}
            </ol>
          ) : (
            <p className="text-slate-500 text-sm">Este procedimiento no tiene pasos escritos.</p>
          )}

          {procedimiento.verificacion_energia_cero ? (
            <div className="flex flex-col gap-1 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="font-semibold text-amber-900 text-sm">Verificación de energía cero</p>
              <p className="text-amber-900">{procedimiento.verificacion_energia_cero}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * BloqueoCard es una «etiqueta roja»: un candado colocado. Activo ⇒ destacado en rojo, con la
 * acción de retirar EN CADA PUNTO aún cubierto (#245: la retirada es punto a punto y el candado
 * se libera con el último); retirado ⇒ atenuado, mostrando quién y cuándo lo retiró. Debajo,
 * las miniaturas de las fotos de verificación adjuntas.
 */
function BloqueoCard({
  bloqueo,
  obraId,
  puedeRegistrar,
  retiroRequiereConexion,
  retiroNoPermitido,
  onRetirarPunto,
}: {
  bloqueo: BloqueoDetalle;
  obraId: number;
  puedeRegistrar: boolean;
  retiroRequiereConexion: boolean;
  // retiroNoPermitido: el candado no es de este usuario y no tiene override (#255 D11).
  retiroNoPermitido: boolean;
  onRetirarPunto: (punto: PuntoDeBloqueo) => void;
}) {
  const activo = bloqueo.activo;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  // Fotos de verificación del candado como imágenes del lightbox (ordinal 1-based, servidas bajo la
  // obra con cookie mismo-origen). Sustituye el antiguo target=_blank por el visor a pantalla completa.
  const fotos: GaleriaImagen[] = Array.from({ length: bloqueo.num_fotos }, (_, i) => i + 1).map(
    (n) => ({
      key: `candado-${bloqueo.id}-${n}`,
      src: `/api/v1/obras/${obraId}/bloqueos/${bloqueo.id}/fotos/${n}`,
      alt: `Foto de verificación ${n} de ${bloqueo.codigo_candado}`,
      origen: "candado" as const,
      titulo: `Candado ${bloqueo.codigo_candado}`,
      pie: textoPuntos(bloqueo),
    }),
  );
  // El motivo de conectividad se muestra SOLO mientras hay un botón «Retirar» apagado por esa
  // causa (#356 D3): el candado tiene que estar activo, quedarle algún punto cubierto y este
  // usuario poder registrar. Sin esa mitad de ausencia, la ficha se llenaría de avisos
  // permanentes sobre botones que no están.
  const hayRetiroPendiente = activo && puedeRegistrar && bloqueo.puntos.some((p) => p.activo);
  return (
    <li
      className={
        activo
          ? "flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 p-4"
          : "flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 opacity-80"
      }
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Lock
            className={activo ? "h-5 w-5 text-red-600" : "h-5 w-5 text-slate-400"}
            aria-hidden="true"
          />
          <span className="font-semibold text-slate-900 tabular-nums">
            {bloqueo.codigo_candado}
          </span>
          {activo ? (
            <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700 text-xs">
              Activo
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-500 text-xs">
              Retirado
            </span>
          )}
        </div>
        {/* Puntos cubiertos por el candado (#244), uno por línea, con su estado propio y su
            acción de retirada (#245): el candado sigue activo mientras le quede algún punto.
            La etiqueta «Retirado» del punto solo se pinta cuando el CANDADO sigue activo: si ya
            está retirado, todos sus puntos lo están y la etiqueta de la tarjeta ya lo dice
            (repetirla por punto sería ruido). El tachado marca el estado en ambos casos. */}
        <ul className="flex flex-col gap-1">
          {bloqueo.puntos.map((p) => (
            <li key={p.id_punto} className="flex flex-wrap items-center gap-1.5 text-sm">
              <span className={p.activo ? "text-slate-700" : "text-slate-400 line-through"}>
                {p.nombre} · {p.descripcion}
              </span>
              {activo && !p.activo ? (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-500 text-xs">
                  Retirado
                </span>
              ) : null}
              {/* #245: cada punto AÚN CUBIERTO se retira por su cuenta; el último libera el
                    candado. Sin conexión, los puntos de un candado ajeno quedan deshabilitados. */}
              {activo && p.activo && puedeRegistrar ? (
                <Button
                  variant="destructive"
                  size="sm"
                  aria-label={`Retirar ${p.nombre} de ${bloqueo.codigo_candado}`}
                  onClick={() => onRetirarPunto(p)}
                  disabled={retiroRequiereConexion || retiroNoPermitido}
                  title={
                    retiroRequiereConexion
                      ? MENSAJE_REQUIERE_CONEXION
                      : retiroNoPermitido
                        ? MOTIVO_RETIRO_AJENO
                        : undefined
                  }
                >
                  Retirar
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
        {/* Los motivos del retiro, en TEXTO y en la tarjeta del candado que apagan (#356 D2/D3),
            no solo en el `title` de cada botón: una línea por candado, no una por punto, porque
            el veto es del candado entero, y una línea POR MOTIVO, no una frase compuesta.
            Cuando concurren los dos —sin red y candado ajeno sin override— se muestran LOS DOS
            (#382 D1): enseñar solo el de conectividad manda al operario a buscar red para
            encontrarse el botón igual de apagado, y enseñar solo el de propiedad le ocultaría
            que además ahora mismo no hay conexión. El de PROPIEDAD va primero (#382 D2) porque
            es el que NO desaparece al cambiar de circunstancia: es el que hay que leer antes
            para no moverse en balde. El `title` conserva su precedencia inversa, sin cambios. */}
        {hayRetiroPendiente && retiroNoPermitido ? (
          <p className="text-slate-500 text-sm">{MOTIVO_RETIRO_AJENO}</p>
        ) : null}
        {hayRetiroPendiente && retiroRequiereConexion ? (
          <p className="text-slate-500 text-sm">{MOTIVO_RETIRO_AJENO_SIN_CONEXION}</p>
        ) : null}
        <span className="text-slate-500 text-sm">
          Colocado por {bloqueo.operario || "—"} · {formatFecha(bloqueo.fecha_colocacion)}
        </span>
        {bloqueo.etiqueta ? (
          <span className="text-slate-500 text-sm">Etiqueta: {bloqueo.etiqueta}</span>
        ) : null}
        {!activo ? (
          <span className="text-slate-500 text-sm">
            Retirado por {bloqueo.retirado_por_nombre || "—"} · {formatFecha(bloqueo.fecha_retiro)}
          </span>
        ) : null}
      </div>

      {/* Miniaturas de las fotos de verificación: abren el lightbox a pantalla completa. */}
      {fotos.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {fotos.map((foto, i) => (
            <button
              key={foto.key}
              type="button"
              onClick={() => setLightboxIndex(i)}
              className="block h-16 w-16 overflow-hidden rounded-lg border border-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <img src={foto.src} alt={foto.alt} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}

      <Lightbox
        imagenes={fotos}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
      />
    </li>
  );
}
